import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Bookmark, CheckCircle2, Download, FileText, MessageCircle, Upload } from "lucide-react";
import { youtubeEmbedUrl, youtubeWatchUrl } from "@/lib/youtube";

export default function Lesson() {
  const { slug, lessonId } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [assignment, setAssignment] = useState({ notes: "", drive_link: "" });

  const load = useCallback(async () => {
    const [c, my, mn] = await Promise.all([
      api.get(`/courses/${slug}`),
      api.get(`/my/enrollments`).catch(() => ({ data: [] })),
      api.get(`/my/notes`, { params: { lesson_id: lessonId } }).catch(() => ({ data: [] })),
    ]);
    setCourse(c.data);
    setEnrollment(my.data.find((e) => e.course_slug === slug) || null);
    setNotes(mn.data);
  }, [slug, lessonId]);
  useEffect(() => { load(); }, [load]);

  const { lesson, prev, next } = useMemo(() => {
    if (!course) return { lesson: null, prev: null, next: null };
    const flat = course.modules.flatMap((m) => m.lessons);
    const idx = flat.findIndex((l) => l.id === lessonId);
    return { lesson: flat[idx], prev: flat[idx - 1], next: flat[idx + 1] };
  }, [course, lessonId]);

  if (!course || !lesson) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const done = new Set(enrollment?.completed_lessons || []);
  const isCompleted = done.has(lesson.id);
  const embedUrl = youtubeEmbedUrl(lesson.video_id);
  const watchUrl = youtubeWatchUrl(lesson.video_id);

  async function complete() {
    await api.post(`/courses/${course.id}/complete`, { lesson_id: lesson.id });
    toast.success("Lesson marked complete! +10 points");
    load();
  }

  async function saveNote() {
    if (!note.trim()) return;
    await api.post("/notes", { course_id: course.id, lesson_id: lesson.id, text: note });
    setNote(""); toast.success("Note saved");
    load();
  }

  async function toggleBookmark() {
    await api.post("/bookmarks", { course_id: course.id, lesson_id: lesson.id });
    toast.success("Bookmark toggled");
  }

  async function submitAssignment() {
    if (!assignment.notes && !assignment.drive_link) return toast.error("Add notes or a link");
    await api.post("/assignments/submit", {
      course_id: course.id, lesson_id: lesson.id,
      notes: assignment.notes, drive_link: assignment.drive_link,
    });
    setAssignment({ notes: "", drive_link: "" });
    toast.success("Assignment submitted for review");
  }

  return (
    <div className="space-y-6" data-testid="lesson-page">
      <Link to={`/app/courses/${slug}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> {course.title}
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-3xl overflow-hidden border border-border shadow-xl bg-black">
            <div className="aspect-video w-full">
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  title={lesson.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="w-full h-full"
                  data-testid="lesson-video"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-white/70 gap-3 p-6 text-center">
                  <p className="text-sm">This lesson doesn't have a valid YouTube video yet.</p>
                </div>
              )}
            </div>
          </div>
          {embedUrl && watchUrl && (
            <p className="text-xs text-muted-foreground -mt-2">
              Trouble playing?{" "}
              <a href={watchUrl} target="_blank" rel="noreferrer"
                 className="text-primary hover:underline" data-testid="open-on-youtube">
                Open on YouTube
              </a>.
            </p>
          )}

          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{lesson.title}</h1>
            <p className="text-muted-foreground mt-2">{lesson.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={complete} disabled={isCompleted} data-testid="mark-complete-btn"
              className={`rounded-full ${isCompleted ? "bg-emerald-500 text-white" : "royal-gradient text-white"}`}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> {isCompleted ? "Completed" : "Mark as complete"}
            </Button>
            <Button variant="outline" onClick={toggleBookmark} className="rounded-full" data-testid="bookmark-btn">
              <Bookmark className="h-4 w-4 mr-1.5" /> Bookmark
            </Button>
            {prev && <Button variant="ghost" onClick={() => nav(`/app/courses/${slug}/lesson/${prev.id}`)} className="rounded-full" data-testid="prev-lesson-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Previous
            </Button>}
            {next && <Button variant="ghost" onClick={() => nav(`/app/courses/${slug}/lesson/${next.id}`)} className="rounded-full ml-auto" data-testid="next-lesson-btn">
              Next <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>}
          </div>

          <Tabs defaultValue="notes" className="mt-6">
            <TabsList className="rounded-full bg-secondary/60">
              <TabsTrigger value="notes" className="rounded-full">Notes</TabsTrigger>
              <TabsTrigger value="resources" className="rounded-full">Resources</TabsTrigger>
              <TabsTrigger value="assignment" className="rounded-full">Assignment</TabsTrigger>
              <TabsTrigger value="discussion" className="rounded-full">Discussion</TabsTrigger>
            </TabsList>
            <TabsContent value="notes" className="mt-4 space-y-3">
              <Textarea data-testid="note-input" placeholder="Write a private note..." value={note} onChange={(e) => setNote(e.target.value)} className="rounded-2xl" />
              <Button onClick={saveNote} className="rounded-full">Save note</Button>
              <div className="space-y-2 mt-4">
                {notes.map((n) => (
                  <div key={n.id} className="rounded-2xl bg-card border border-border p-4 text-sm">
                    <p>{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-1.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
                {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
              </div>
            </TabsContent>
            <TabsContent value="resources" className="mt-4">
              <div className="rounded-2xl bg-card border border-border p-5 flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center"><FileText className="h-5 w-5 text-primary" /></div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">Lesson Workbook (PDF)</p>
                  <p className="text-xs text-muted-foreground">Notes, assignments and templates</p>
                </div>
                <Button variant="outline" className="rounded-full" size="sm"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
              </div>
            </TabsContent>
            <TabsContent value="assignment" className="mt-4 space-y-3">
              <Textarea data-testid="assignment-notes" placeholder="Describe your submission..." value={assignment.notes} onChange={(e) => setAssignment({ ...assignment, notes: e.target.value })} className="rounded-2xl" />
              <input data-testid="assignment-link" placeholder="Google Drive link" value={assignment.drive_link} onChange={(e) => setAssignment({ ...assignment, drive_link: e.target.value })}
                className="w-full h-11 rounded-xl border border-border bg-transparent px-3 text-sm" />
              <Button onClick={submitAssignment} data-testid="submit-assignment-btn" className="rounded-full royal-gradient border-0 text-white">
                <Upload className="h-4 w-4 mr-1.5" /> Submit for review
              </Button>
            </TabsContent>
            <TabsContent value="discussion" className="mt-4">
              <div className="rounded-2xl bg-card border border-border p-8 text-center">
                <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground" />
                <p className="mt-3 font-semibold">Join the discussion on WhatsApp</p>
                <p className="text-sm text-muted-foreground">Ask doubts and get answers from mentors & peers.</p>
                <a href="https://wa.me/919999999999" target="_blank" rel="noreferrer">
                  <Button className="mt-4 rounded-full">Open WhatsApp community</Button>
                </a>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Curriculum sidebar */}
        <div className="lg:sticky lg:top-24 h-fit rounded-3xl bg-card border border-border p-4 max-h-[80vh] overflow-y-auto">
          <p className="font-display font-bold px-2 pb-3">Curriculum</p>
          {course.modules.map((m) => (
            <div key={m.id} className="mb-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground px-2 mb-1.5">{m.title}</p>
              {m.lessons.map((l) => {
                const isCur = l.id === lesson.id;
                const isDone = done.has(l.id);
                return (
                  <Link key={l.id} to={`/app/courses/${slug}/lesson/${l.id}`}
                        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                          isCur ? "bg-primary text-primary-foreground" : "hover:bg-secondary/60"
                        }`}>
                    <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${isDone ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                      {isDone ? <CheckCircle2 className="h-3 w-3" /> : "▶"}
                    </span>
                    <span className="line-clamp-2">{l.title}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
